import React, { useRef, useState } from 'react'
import { useEffectOnce, useInterval } from 'react-use'
import { reportError } from 'utils/error'
import { Importer } from '../ContentSourcePicker/ContentSourcePicker'
import Styles from './Step2.module.scss'
import { Processing } from './Processing/Processing'
import { useCreateBrandVoiceAnalysisSync } from 'hooks/queries/brandVoiceAnalyses/useCreateBrandVoiceAnalysisSync'
import { useCreateSourceFromImport } from 'hooks/queries/workspaces/useCreateSourceFromImport'

export type voiceParams = {
  purpose: string[]
  audience: string[]
  tone: string[]
  emotions: string[]
  character: string[]
  syntax: string[]
  language: string[]
  name: string
  emoji?: string | null
}

export type Step2Props = {
  importer: Importer
  sample: string
  onNext: (voiceParams: voiceParams) => void
  onPrev: () => void
}

export const Step2: React.FC<React.PropsWithChildren<Step2Props>> = ({ importer, sample, onNext, onPrev }) => {
  const { mutateAsync: createSource } = useCreateSourceFromImport()
  const { mutateAsync: createBrandVoiceAnalysis } = useCreateBrandVoiceAnalysisSync()
  const [error, setError] = useState<string | Error | unknown | null>(null)
  if (error) {
    throw error
  }

  const [currentSpinnerSlide, setCurrentSpinnerSlide] = useState(0)
  useInterval(
    () => {
      setCurrentSpinnerSlide(currentSpinnerSlide + 1)
    },
    currentSpinnerSlide === 3 ? null : 1 * 1000, // 1 second per slide
  )
  const abortController = useRef(new AbortController())

  const cancelRequest = () => {
    abortController.current.abort()
    onPrev()
  }

  useEffectOnce(() => {
    ;(async () => {
      try {
        const sourceResult = await createSource({ content: sample, importer, signal: abortController.current.signal })
        const analysis = await createBrandVoiceAnalysis({
          sources: [{ id: sourceResult.source.id, type: sourceResult.source.type }],
          signal: abortController.current.signal,
        })

        onNext(analysis.result)
      } catch (e) {
        reportError(e, {
          metaData: { source: 'voice_modal' },
        })
        setError(e)
      }
    })()
  })

  return (
    <div className={Styles.Step2}>
      <Processing step={currentSpinnerSlide} onCancel={cancelRequest} />
    </div>
  )
}
